import { useSyncExternalStore } from 'react';

import { ClinicInfo } from '@/types/referrals';
import { safeLocalStorage } from './safeStorage';

// "Recently viewed" clinics for the admin Clinics page zero state. Stored in
// localStorage as small snapshots (only the fields the list renders — never
// internalNotes or other sensitive detail fields). Order encodes recency:
// most recent first. Snapshots are only a first-paint fallback — the list
// page overlays fresh clinic data fetched by id — so staleness here is
// limited to a brief loading flash.
//
// The stored payload is scoped to the user who recorded it (ownerId): reads
// return nothing for anyone else, so the list can't leak to the next admin
// on a shared browser regardless of how the previous session ended (explicit
// logout, API-401 session expiry, or the browser simply being closed).
const STORAGE_KEY = 'admin.recentClinics';
const MAX_RECENT_CLINICS = 8;

// Single source of truth for the snapshot whitelist: drives both the
// RecentClinic type and the runtime copy in recordRecentClinic, so the two
// can't drift (all these fields are optional on ClinicInfo, which means a
// missed copy would compile cleanly).
const RECENT_CLINIC_FIELDS = [
  'id',
  'name',
  'category',
  'address',
  'address1',
  'city',
  'state',
  'zip',
  'phone',
  'faxNumber',
] as const;

export type RecentClinic = Pick<
  ClinicInfo,
  (typeof RECENT_CLINIC_FIELDS)[number]
>;

interface RecentClinicsEnvelope {
  ownerId: string;
  clinics: RecentClinic[];
}

// useSyncExternalStore requires getSnapshot to return a stable reference for
// unchanged state, so cache the parsed envelope keyed by the raw stored
// string, and hand out one shared empty array.
const EMPTY: RecentClinic[] = [];
let cachedRaw: string | null | undefined;
let cachedEnvelope: RecentClinicsEnvelope | null = null;

const listeners = new Set<() => void>();

function emitChange(): void {
  listeners.forEach(listener => listener());
}

function parseEnvelope(raw: string): RecentClinicsEnvelope | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { ownerId, clinics } = parsed as {
      ownerId?: unknown;
      clinics?: unknown;
    };
    if (typeof ownerId !== 'string' || !Array.isArray(clinics)) return null;
    return {
      ownerId,
      clinics: clinics.filter(
        (entry): entry is RecentClinic =>
          typeof entry === 'object' &&
          entry !== null &&
          typeof (entry as { id?: unknown }).id === 'string'
      ),
    };
  } catch {
    return null;
  }
}

function readEnvelope(): RecentClinicsEnvelope | null {
  const raw = safeLocalStorage.getItem(STORAGE_KEY);
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedEnvelope = raw ? parseEnvelope(raw) : null;
  }
  return cachedEnvelope;
}

export function readRecentClinics(userId: string | undefined): RecentClinic[] {
  if (!userId) return EMPTY;
  const envelope = readEnvelope();
  if (!envelope || envelope.ownerId !== userId) return EMPTY;
  return envelope.clinics;
}

export function recordRecentClinic(clinic: ClinicInfo, userId: string): void {
  const snapshot = Object.fromEntries(
    RECENT_CLINIC_FIELDS.map(field => [field, clinic[field] ?? null])
  ) as RecentClinic;
  // readRecentClinics returns [] for a different previous owner, so a new
  // user's first visit atomically replaces the old user's list.
  const next: RecentClinicsEnvelope = {
    ownerId: userId,
    clinics: [
      snapshot,
      ...readRecentClinics(userId).filter(entry => entry.id !== clinic.id),
    ].slice(0, MAX_RECENT_CLINICS),
  };
  if (!safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(next))) return;
  emitChange();
}

export function clearRecentClinics(): void {
  safeLocalStorage.removeItem(STORAGE_KEY);
  emitChange();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // 'storage' fires only in OTHER tabs; the local emitter covers this one.
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

// Live view of the current user's recent-clinics list: updates on same-tab
// record/clear and on changes made from other tabs.
export function useRecentClinics(userId: string | undefined): RecentClinic[] {
  return useSyncExternalStore(subscribe, () => readRecentClinics(userId));
}
